import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const GA_ID = process.env.REACT_APP_GA_ID || ""; // e.g. G-XXXXXXXXXX
const GTM_ID = process.env.REACT_APP_GTM_ID || ""; // e.g. GTM-XXXXXXX

/**
 * Analytics — injects GA4 + GTM exactly once (if their env vars are set),
 * then tracks SPA pageviews on every route change.
 *
 * Setting the env vars in production:
 *   Emergent → Deploy → Environment Variables
 *     REACT_APP_GA_ID  = G-XXXXXXXXXX
 *     REACT_APP_GTM_ID = GTM-XXXXXXX
 *
 * Any submission/CTA event can be fired with:
 *   import { trackEvent } from "@/components/Analytics";
 *   trackEvent("quote_review_submit", { value: 1500 });
 */
let injected = false;

function injectScripts() {
  if (injected || typeof window === "undefined") return;
  injected = true;

  if (GA_ID) {
    // Standard GA4 tag
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", GA_ID, {
      send_page_view: false, // we handle SPA pageviews manually
      anonymize_ip: true,
    });
  }

  if (GTM_ID) {
    // Standard GTM head snippet
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(s);

    // noscript fallback iframe
    const noscript = document.createElement("noscript");
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }
}

export default function Analytics() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    injectScripts();
  }, []);

  useEffect(() => {
    const url = pathname + search;
    if (GA_ID && window.gtag) {
      window.gtag("event", "page_view", {
        page_location: window.location.href,
        page_path: url,
        page_title: document.title,
      });
    }
    if (GTM_ID && window.dataLayer) {
      window.dataLayer.push({
        event: "page_view",
        page_path: url,
        page_title: document.title,
      });
    }
  }, [pathname, search]);

  return null;
}

/** Fire a custom event to both GA4 and GTM (whichever is installed). */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  if (GA_ID && window.gtag) {
    window.gtag("event", name, params);
  }
  if (GTM_ID && window.dataLayer) {
    window.dataLayer.push({ event: name, ...params });
  }
}
